<template>
  <div>
    <!-- Enregistrement des colonnes (invisible) -->
    <slot></slot>

    <!-- Barre d'outils : actions groupées (gauche) + sélecteur de colonnes optionnelles (droite, desktop only) -->
    <div class="ent-list-toolbar" v-if="hasToolbar">
      <div class="ent-list-toolbar__left">
        <!-- Menu générique d'actions groupées : piloté par la sélection interne. -->
        <b-dropdown
          v-if="bulkActions && bulkActions.length > 0"
          no-caret
          variant="link"
          toggle-class="ent-toolbar-btn"
          menu-class="ent-bulk-menu"
          :disabled="internalSelectedItems.length === 0"
        >
          <template #button-content>
            <font-awesome-icon icon="tasks" /> {{ bulkActionsLabel }}
            <span v-if="internalSelectedItems.length" class="ent-toolbar-count">{{ internalSelectedItems.length }}</span>
            <font-awesome-icon icon="chevron-down" class="ent-toolbar-caret" />
          </template>
          <b-dropdown-item v-for="action in bulkActions" :key="action.key" @click="$emit('bulk-action', action.key)">
            <font-awesome-icon v-if="action.icon" :icon="action.icon" class="ent-bulk-item-icon" />
            {{ action.label }}
          </b-dropdown-item>
        </b-dropdown>
        <!-- Slot pour la suite domaine-spécifique (ex. picker de destination + Appliquer). -->
        <slot name="toolbar-left" :selected="internalSelectedItems"></slot>
      </div>
      <div class="ent-list-toolbar__right d-none d-md-flex" v-if="optionalColumns && optionalColumns.length > 0">
        <b-dropdown right no-caret variant="link" toggle-class="ent-toolbar-btn" menu-class="ent-cols-menu">
          <template #button-content>
            <font-awesome-icon icon="table" /> {{ columnsLabel }}
            <span v-if="activeOptionalKeys.length" class="ent-toolbar-count">{{ activeOptionalKeys.length }}</span>
            <font-awesome-icon icon="chevron-down" class="ent-toolbar-caret" />
          </template>
          <b-dropdown-form>
            <div class="ent-cols-actions">
              <a href="#" @click.prevent="showAllOptional">{{ selectAllLabel }}</a>
              <span>·</span>
              <a href="#" @click.prevent="hideAllOptional">{{ clearLabel }}</a>
            </div>
            <div class="ent-cols-grid">
              <b-form-checkbox
                v-for="opt in optionalColumnDescriptors"
                :key="opt._key"
                :checked="isOptionalActive(opt.field)"
                @change="toggleOptional(opt.field)"
              >
                {{ opt.label }}
              </b-form-checkbox>
            </div>
          </b-dropdown-form>
        </b-dropdown>
      </div>
    </div>

    <!-- Barre de filtres actifs (best practice : voir/retirer ce qui est appliqué). -->
    <div v-if="activeFilterChips.length" class="ent-active-filters d-none d-md-flex">
      <span v-for="chip in activeFilterChips" :key="chip.field" class="ent-filter-chip" @click="clearColumnFilter(chip.field)">
        <span class="ent-filter-chip__label">{{ chip.label }}</span>
        <span class="ent-filter-chip__value">{{ chip.value }}</span>
        <font-awesome-icon icon="times" class="ent-filter-chip__x" />
      </span>
      <a href="#" class="ent-active-filters__clear" @click.prevent="clearAllFilters">{{ clearAllFiltersLabel }}</a>
    </div>

    <!-- Desktop : ≥ 768px -->
    <div class="d-none d-md-block" v-if="items && items.length > 0">
      <div class="ent-card" :style="cardStyle">
        <div class="ent-table-wrapper">
          <table class="ent-table" :class="tableClass">
            <thead>
              <tr>
                <th v-if="selectable" class="ent-th--check">
                  <input type="checkbox" :checked="internalSelectAll" @click="selectAllItems()" />
                </th>
                <th v-for="col in displayedColumns" :key="col._key" :class="[col.thClass, col.sortable ? 'ent-th--sortable' : '']">
                  <span class="ent-th__inner">
                    <span class="ent-th__label" @click="col.sortable ? $emit('sort', col.field) : undefined">
                      {{ col.label }}
                      <font-awesome-icon
                        v-if="col.sortable && col.field"
                        :icon="sortField === col.field ? (sortDesc ? 'sort-down' : 'sort-up') : 'sort'"
                        class="ent-sort-icon"
                      />
                    </span>
                    <!-- Filtre par colonne : entonnoir → popover avec le champ (filtre serveur « contient »). -->
                    <b-dropdown
                      v-if="col.filterable && col.field"
                      no-caret
                      variant="link"
                      :toggle-class="['ent-col-filter-toggle', isColumnFiltered(col.field) ? 'ent-col-filter-toggle--active' : '']"
                      menu-class="ent-col-filter-menu"
                      @shown="focusColumnFilter(col.field)"
                    >
                      <template #button-content>
                        <font-awesome-icon icon="filter" />
                      </template>
                      <b-dropdown-form @submit.prevent>
                        <input
                          :ref="`colFilter:${col.field}`"
                          type="text"
                          class="ent-col-filter"
                          :value="columnFilterValues[col.field] || ''"
                          :placeholder="col.label"
                          @input="onColumnFilterInput(col.field, $event.target.value)"
                        />
                        <a
                          v-if="isColumnFiltered(col.field)"
                          href="#"
                          class="ent-col-filter-clear"
                          @click.prevent="clearColumnFilter(col.field)"
                        >
                          {{ clearFilterLabel }}
                        </a>
                      </b-dropdown-form>
                    </b-dropdown>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in items" :key="item.id">
                <tr :class="{ 'ent-row--selected': isItemSelected(item) }">
                  <td v-if="selectable" class="ent-td--check">
                    <input type="checkbox" :checked="isItemSelected(item)" @click="toggleItem(item, $event)" />
                  </td>
                  <td v-for="col in displayedColumns" :key="col._key" :class="col.tdClass">
                    <ent-list-cell :column="col" :item="item" />
                  </td>
                </tr>
                <!-- Sous-lignes (ex. variantes) : mêmes colonnes que la ligne parente, donc
                     l'alignement suit automatiquement les colonnes affichées. -->
                <tr v-for="(sub, sIdx) in subItemsOf(item)" :key="item.id + '-sub-' + sIdx" class="ent-row--sub">
                  <td v-if="selectable"></td>
                  <td v-for="col in displayedColumns" :key="col._key" :class="col.tdClass">
                    <ent-list-sub-cell :column="col" :item="item" :sub-item="sub" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Mobile : < 768px -->
    <div class="d-block d-md-none" v-if="items && items.length > 0">
      <div v-for="item in items" :key="item.id" class="ent-mobile-card" :class="{ 'ent-mobile-card--selected': isItemSelected(item) }">
        <div class="ent-mobile-card__header">
          <input v-if="selectable" type="checkbox" :checked="isItemSelected(item)" @click="toggleItem(item, $event)" />
          <span class="ent-mobile-card__title" v-if="titleColumn">
            <ent-list-cell :column="titleColumn" :item="item" />
          </span>
          <ent-list-cell v-for="col in badgeColumns" :key="col._key" :column="col" :item="item" />
        </div>
        <div class="ent-mobile-card__body" v-if="detailColumns.length">
          <div v-for="col in detailColumns" :key="col._key" class="ent-mobile-card__field">
            <span class="ent-mobile-card__label">{{ col.label }}</span>
            <ent-list-cell :column="col" :item="item" />
          </div>
        </div>
        <!-- Sous-lignes en mobile : une ligne par sous-élément, colonnes concernées uniquement. -->
        <div class="ent-mobile-card__subs" v-if="subItemsOf(item).length">
          <div v-for="(sub, sIdx) in subItemsOf(item)" :key="item.id + '-msub-' + sIdx" class="ent-mobile-card__sub">
            <template v-for="col in subCellColumns" :key="col._key">
              <ent-list-sub-cell :column="col" :item="item" :sub-item="sub" />
            </template>
          </div>
        </div>
        <div class="ent-mobile-card__actions" v-if="actionColumns.length">
          <ent-list-cell v-for="col in actionColumns" :key="col._key" :column="col" :item="item" />
        </div>
      </div>
    </div>

    <!-- État chargement -->
    <div v-if="loading && (!items || items.length === 0)" class="ent-loading">
      <font-awesome-icon icon="sync" spin />
    </div>

    <!-- État vide -->
    <div v-if="!loading && (!items || items.length === 0)" class="ent-empty">
      <slot name="empty">
        <p>Aucun résultat</p>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h } from 'vue';
import { BulkAction, OptionalColumn } from './ent-list.types';

// Vue 3 : un composant fonctionnel est une simple fonction (props) => VNode.
const EntListCell = (props: any) => {
  const { column, item } = props;
  if (!column || !column.renderCell) return h('span', '—');
  const result = column.renderCell(item);
  if (result == null || result === '') return h('span', '—');
  if (typeof result === 'string' || typeof result === 'number') return h('span', String(result));
  if (Array.isArray(result)) {
    if (result.length === 0) return h('span', '—');
    if (result.length === 1) return result[0];
    return h('span', { style: { display: 'contents' } }, result);
  }
  return result;
};
(EntListCell as any).props = ['column', 'item'];

// Cellule de sous-ligne : rend le slot `subcell` de la colonne, ou rien du tout.
// Contrairement à EntListCell, l'absence de contenu ne produit PAS de tiret : une sous-ligne
// n'a de valeur que dans les quelques colonnes concernées, les autres doivent rester vides.
const EntListSubCell = (props: any) => {
  const { column, item, subItem } = props;
  if (!column || !column.hasSubCell || !column.renderSubCell) return null;
  const result = column.renderSubCell(item, subItem);
  if (result == null || result === '') return null;
  if (typeof result === 'string' || typeof result === 'number') return h('span', String(result));
  if (Array.isArray(result)) {
    if (result.length === 0) return null;
    if (result.length === 1) return result[0];
    return h('span', { style: { display: 'contents' } }, result);
  }
  return result;
};
(EntListSubCell as any).props = ['column', 'item', 'subItem'];

/** Lit une valeur potentiellement imbriquée via un chemin pointé, sans lever si un maillon est nul. */
function getByPath(obj: any, path: string): any {
  if (obj == null) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export default defineComponent({
  components: { EntListCell, EntListSubCell },
  provide() {
    const self = this as any;
    return {
      entListRegisterColumn: (col: any) => {
        col._key = col.field || col.label || String(self.registeredColumns.length);
        self.registeredColumns.push(col);
      },
    };
  },
  props: {
    items: { type: Array as () => any[], required: true },
    loading: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    sortField: { type: String, default: '' },
    sortDesc: { type: Boolean, default: false },
    cardStyle: { type: String, default: '' },
    tableClass: { type: String, default: '' },
    /** Colonnes optionnelles proposées dans le sélecteur. Vide => pas de sélecteur (comportement historique). */
    optionalColumns: { type: Array as () => OptionalColumn[], default: () => [] },
    /** Clé de persistance localStorage du choix de colonnes. Vide => pas de persistance. */
    storageKey: { type: String, default: '' },
    columnsLabel: { type: String, default: 'Colonnes' },
    selectAllLabel: { type: String, default: 'Tout' },
    clearLabel: { type: String, default: 'Aucune' },
    clearFilterLabel: { type: String, default: 'Effacer' },
    clearAllFiltersLabel: { type: String, default: 'Tout effacer' },
    /** Actions groupées proposées quand des lignes sont sélectionnées. Vide => pas de menu d'actions. */
    bulkActions: { type: Array as () => BulkAction[], default: () => [] },
    bulkActionsLabel: { type: String, default: 'Actions groupées' },
    /**
     * Extrait les sous-éléments d'une ligne (ex. variantes d'un produit). Chaque sous-élément
     * donne une sous-ligne rendue par le slot `#subcell` des colonnes qui en déclarent un.
     * Absent => aucune sous-ligne (comportement historique).
     */
    subItems: { type: Function as unknown as () => (item: any) => any[], default: null },
  },
  data() {
    return {
      /** Colonnes déclarées via <ent-column> (toujours affichées). */
      registeredColumns: [] as any[],
      /** Champs des colonnes optionnelles actuellement activées. */
      activeOptionalKeys: [] as string[],
      internalSelectedItems: [] as any[],
      lastCheckedIndex: -1,
      /** Valeurs saisies dans la ligne de filtres, indexées par champ de colonne. */
      columnFilterValues: {} as Record<string, string>,
      filterDebounce: null as any,
    };
  },
  computed: {
    storageId(): string {
      return this.storageKey ? `ent-list:cols:${this.storageKey}` : '';
    },
    /** La barre d'outils s'affiche s'il y a des colonnes optionnelles, des actions groupées ou un slot d'actions. */
    hasToolbar(): boolean {
      return (
        (this.optionalColumns && this.optionalColumns.length > 0) ||
        (this.bulkActions && this.bulkActions.length > 0) ||
        !!this.$slots['toolbar-left']
      );
    },
    /** Descripteurs de rendu des colonnes optionnelles (toutes, actives ou non). */
    optionalColumnDescriptors(): any[] {
      return this.optionalColumns.map(opt => ({
        _key: `opt:${opt.field}`,
        field: opt.field,
        label: opt.label,
        mobileRole: opt.mobileRole || 'detail',
        sortable: opt.sortable === true,
        filterable: opt.filterable === true,
        thClass: '',
        tdClass: opt.tdClass || '',
        renderCell: (item: any) => {
          const value = getByPath(item, opt.field);
          if (value == null || value === '') return null;
          return opt.formatter ? opt.formatter(value, item) : String(value);
        },
      }));
    },
    /**
     * Colonnes réellement affichées : déclarées + optionnelles actives.
     * Les optionnelles s'insèrent toujours AVANT la (les) colonne(s) d'action, qui restent en fin de tableau.
     */
    displayedColumns(): any[] {
      const active = this.optionalColumnDescriptors.filter(c => this.activeOptionalKeys.includes(c.field));
      const declared = this.registeredColumns;
      const firstActionIndex = declared.findIndex(c => c.mobileRole === 'action');
      if (firstActionIndex === -1) {
        return [...declared, ...active];
      }
      return [...declared.slice(0, firstActionIndex), ...active, ...declared.slice(firstActionIndex)];
    },
    /** Pastilles des filtres actifs (label de la colonne + valeur), pour la barre de filtres. */
    activeFilterChips(): { field: string; label: string; value: string }[] {
      return this.displayedColumns
        .filter(c => c.field && this.isColumnFiltered(c.field))
        .map(c => ({ field: c.field, label: c.label, value: this.columnFilterValues[c.field].trim() }));
    },
    internalSelectAll(): boolean {
      return this.items != null && this.items.length > 0 && this.internalSelectedItems.length === this.items.length;
    },
    // Vue mobile (cartes) : uniquement les colonnes déclarées.
    titleColumn(): any {
      return this.registeredColumns.find(c => c.mobileRole === 'title') || null;
    },
    badgeColumns(): any[] {
      return this.registeredColumns.filter(c => c.mobileRole === 'badge');
    },
    detailColumns(): any[] {
      return this.registeredColumns.filter(c => c.mobileRole === 'detail');
    },
    actionColumns(): any[] {
      return this.registeredColumns.filter(c => c.mobileRole === 'action');
    },
    /** Colonnes déclarant un slot `subcell` : les seules à rendre sur une sous-ligne mobile. */
    subCellColumns(): any[] {
      return this.registeredColumns.filter(c => c.hasSubCell);
    },
  },
  watch: {
    items() {
      this.onItemsChanged();
    },
  },
  created() {
    this.loadPersistedColumns();
  },
  methods: {
    /** Sous-éléments d'une ligne, toujours un tableau pour simplifier les `v-for`. */
    subItemsOf(item: any): any[] {
      if (!this.subItems) return [];
      return (this.subItems as (i: any) => any[])(item) || [];
    },
    loadPersistedColumns(): void {
      if (!this.storageId) return;
      try {
        const raw = localStorage.getItem(this.storageId);
        if (!raw) return;
        const saved: string[] = JSON.parse(raw);
        const available = new Set(this.optionalColumns.map(c => c.field));
        this.activeOptionalKeys = saved.filter(f => available.has(f));
      } catch {
        // localStorage indisponible ou valeur corrompue : on repart des colonnes par défaut.
      }
    },
    persistColumns(): void {
      if (!this.storageId) return;
      try {
        localStorage.setItem(this.storageId, JSON.stringify(this.activeOptionalKeys));
      } catch {
        // localStorage indisponible (mode privé, quota) : on ignore, l'affichage reste fonctionnel.
      }
    },
    isOptionalActive(field: string): boolean {
      return this.activeOptionalKeys.includes(field);
    },
    toggleOptional(field: string): void {
      if (this.isOptionalActive(field)) {
        this.activeOptionalKeys = this.activeOptionalKeys.filter(f => f !== field);
      } else {
        this.activeOptionalKeys = [...this.activeOptionalKeys, field];
      }
      this.persistColumns();
    },
    showAllOptional(): void {
      this.activeOptionalKeys = this.optionalColumns.map(c => c.field);
      this.persistColumns();
    },
    hideAllOptional(): void {
      this.activeOptionalKeys = [];
      this.persistColumns();
    },
    onItemsChanged(): void {
      this.internalSelectedItems = [];
      this.lastCheckedIndex = -1;
      this.$emit('selection-change', []);
    },
    isColumnFiltered(field: string): boolean {
      return (this.columnFilterValues[field] || '').trim() !== '';
    },
    focusColumnFilter(field: string): void {
      this.$nextTick(() => {
        const ref = this.$refs[`colFilter:${field}`] as HTMLInputElement[] | HTMLInputElement | undefined;
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) el.focus();
      });
    },
    onColumnFilterInput(field: string, value: string): void {
      this.columnFilterValues = { ...this.columnFilterValues, [field]: value };
      if (this.filterDebounce) {
        clearTimeout(this.filterDebounce);
      }
      this.filterDebounce = setTimeout(() => this.emitFilters(), 350);
    },
    clearColumnFilter(field: string): void {
      const next = { ...this.columnFilterValues };
      delete next[field];
      this.columnFilterValues = next;
      this.emitFilters();
    },
    clearAllFilters(): void {
      this.columnFilterValues = {};
      this.emitFilters();
    },
    emitFilters(): void {
      const filters = Object.keys(this.columnFilterValues)
        .filter(field => (this.columnFilterValues[field] || '').trim() !== '')
        .map(field => ({ field, value: this.columnFilterValues[field].trim() }));
      this.$emit('filters-change', filters);
    },
    isItemSelected(item: any): boolean {
      if (item.id == null) return false;
      return this.internalSelectedItems.some(i => i.id === item.id);
    },
    toggleItem(item: any, event: MouseEvent): void {
      const currentIndex = this.items.indexOf(item);
      const currentlySelected = this.isItemSelected(item);

      if (event.shiftKey && this.lastCheckedIndex !== -1) {
        const start = Math.min(this.lastCheckedIndex, currentIndex);
        const end = Math.max(this.lastCheckedIndex, currentIndex);
        const range = this.items.slice(start, end + 1);
        if (!currentlySelected) {
          range.forEach(p => {
            if (!this.isItemSelected(p)) this.internalSelectedItems.push(p);
          });
        } else {
          this.internalSelectedItems = this.internalSelectedItems.filter(i => !range.some(p => p.id === i.id));
        }
      } else {
        if (currentlySelected) {
          this.internalSelectedItems = this.internalSelectedItems.filter(i => i.id !== item.id);
        } else {
          this.internalSelectedItems.push(item);
        }
        this.lastCheckedIndex = currentIndex;
      }
      this.$emit('selection-change', [...this.internalSelectedItems]);
    },
    selectAllItems(): void {
      if (this.internalSelectAll) {
        this.internalSelectedItems = [];
      } else {
        this.internalSelectedItems = [...this.items];
      }
      this.$emit('selection-change', [...this.internalSelectedItems]);
    },
  },
});
</script>

<style scoped>
.ent-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.ent-list-toolbar__left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-height: 1px;
}
.ent-list-toolbar__right {
  margin-left: auto;
}

/* Les styles du déclencheur (.ent-toolbar-btn, .ent-toolbar-caret, .ent-toolbar-count) et du menu
   d'actions groupées (.ent-bulk-menu, .ent-bulk-item-icon) sont dans global.scss : pattern partagé
   avec le détail catalogue. */

/* Menu déroulant des colonnes : grille 2 colonnes pour éviter une liste trop longue (16 champs). */
.ent-list-toolbar :deep(.ent-cols-menu) {
  padding: 0.5rem 0;
  border: 1px solid #e9ecef;
  border-radius: 0.6rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  max-height: 80vh;
  overflow-y: auto;
}
.ent-list-toolbar :deep(.ent-cols-menu .b-dropdown-form) {
  padding: 0.25rem 1rem 0.5rem;
}
.ent-cols-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(9rem, 1fr));
  column-gap: 1.5rem;
  row-gap: 0.15rem;
}
/* Ne PAS utiliser le raccourci `padding` : il écraserait le padding-left:1.5rem de Bootstrap
   qui réserve la place de la case à cocher (::before positionné à left:-1.5rem → sinon rogné). */
.ent-list-toolbar :deep(.ent-cols-menu .custom-checkbox) {
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}
.ent-list-toolbar :deep(.ent-cols-menu .custom-control-label) {
  white-space: nowrap;
  cursor: pointer;
}
.ent-list-toolbar :deep(.ent-cols-menu .custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #f28a16;
  border-color: #f28a16;
}

.ent-cols-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding-bottom: 0.4rem;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  border-bottom: 1px solid #e9ecef;
}
.ent-cols-actions a {
  color: #f28a16;
  font-weight: 600;
  text-decoration: none;
}
.ent-cols-actions a:hover {
  color: #d4720a;
  text-decoration: underline;
}
.ent-cols-actions span {
  color: #ced4da;
}

/* Filtre par colonne : entonnoir dans l'en-tête + popover */
.ent-th__inner {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.ent-th__label {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
:deep(.ent-col-filter-toggle) {
  padding: 0.1rem 0.3rem;
  font-size: 0.72rem;
  line-height: 1;
  color: #adb5bd;
  border: none;
  background: transparent;
  box-shadow: none;
}
:deep(.ent-col-filter-toggle:hover) {
  color: #6c757d;
}
:deep(.ent-col-filter-toggle--active) {
  color: #f28a16;
}
:deep(.ent-col-filter-menu) {
  padding: 0.6rem;
  border: 1px solid #e9ecef;
  border-radius: 0.6rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 12rem;
}
.ent-col-filter {
  width: 100%;
  padding: 0.3rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 400;
  color: #333;
  border: 1px solid #dee2e6;
  border-radius: 0.4rem;
  background: #fff;
}
.ent-col-filter:focus {
  outline: none;
  border-color: #f28a16;
  box-shadow: 0 0 0 0.12rem rgba(242, 138, 22, 0.2);
}
.ent-col-filter-clear {
  display: inline-block;
  margin-top: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #f28a16;
  text-decoration: none;
}
.ent-col-filter-clear:hover {
  color: #d4720a;
  text-decoration: underline;
}

/* Barre de filtres actifs */
.ent-active-filters {
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.ent-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  font-size: 0.78rem;
  color: #333;
  background: #fff3e0;
  border: 1px solid #f28a16;
  border-radius: 999px;
  cursor: pointer;
}
.ent-filter-chip:hover {
  background: #ffe6c7;
}
.ent-filter-chip__label {
  color: #6c757d;
}
.ent-filter-chip__value {
  font-weight: 600;
}
.ent-filter-chip__x {
  font-size: 0.7rem;
  color: #d4720a;
}
.ent-active-filters__clear {
  font-size: 0.78rem;
  font-weight: 600;
  color: #f28a16;
  text-decoration: none;
}
.ent-active-filters__clear:hover {
  color: #d4720a;
  text-decoration: underline;
}
</style>
